import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';

import { Select, Store } from '@ngxs/store';
import { filter, Observable, Subject, takeUntil } from 'rxjs';
import { SelectChangeEvent, SelectModule } from 'primeng/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { FormsModule } from '@angular/forms';
import { PeriodsI } from '@models/periods';
import { UserInfo } from '@models/user-info.model';
import { SettingsSelectors } from '@store/selectors/settings.selectors';
import { ToetsenSelectors } from '@store/selectors/toetsen.selectors';
import { AppSelectors } from '@store/selectors';
import { Router } from '@angular/router';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { TabsModule } from 'primeng/tabs';
import { UiActions } from '@store/actions/ui.actions';

@Component({
  selector: 'cito-doe-user-options',
  imports: [SelectModule, FormsModule, DialogModule, ButtonModule, TranslateModule, TabsModule],
  templateUrl: './user-options.component.html',
  styleUrls: ['./user-options.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserOptionsComponent implements OnInit, OnDestroy {
  @Select(AppSelectors.userInfo) userInfo$!: Observable<UserInfo>;
  @Select(SettingsSelectors.settingsPeriods) settingsPeriods$!: Observable<PeriodsI>;
  @Select(ToetsenSelectors.toetsenPeriods) toetsenPeriods$!: Observable<PeriodsI>;

  @Input() styleClass: 'header' | '' = '';

  private readonly destroy$: Subject<void> = new Subject<void>();

  userDropDownItems!: { label: string; icon: string; action: string; dataTest?: string; title?: string }[];

  userInfo: UserInfo | null = null;
  // displayName: string | undefined = undefined;
  userName!: { label: string };

  constructor(
    private store: Store,
    private translate: TranslateService,
    private readonly router: Router,
  ) {}

  ngOnInit() {
    this.userInfo$
      .pipe(
        filter((user) => !!user),
        takeUntil(this.destroy$),
      )
      .subscribe((userInfo) => {
        this.userInfo = userInfo;
        this.userDropDownItems = [
          {
            label: userInfo?.name,
            icon: '',
            action: 'name',
            dataTest: 'user-options-name',
            title: userInfo?.email,
          },
          {
            label: this.translate.instant('USER_MENU.INFO'),
            icon: 'pi pi-fw pi-info-circle',
            action: 'info',
            dataTest: 'user-options-info',
          },
          {
            label: this.translate.instant('USER_MENU.LOGOUT'),
            icon: 'pi pi-fw pi-users',
            action: 'logout',
            dataTest: 'user-options-logout',
          },
        ];
      });
  }

  onUserDropdownItemSelected(event: SelectChangeEvent) {
    this.userName = { label: this.userInfo?.name || '' };

    if (event.value.action === 'logout') {
      this.store.dispatch(
        new UiActions.confirmDialogOpen({
          header: this.translate.instant('CONFIRM_DIALOG_LOGOUT.HEADER'),
          message: this.translate.instant('CONFIRM_DIALOG_LOGOUT.MESSAGE'),
          acceptLabel: this.translate.instant('CONFIRM_DIALOG_LOGOUT.ACCEPT'),
          rejectLabel: this.translate.instant('CONFIRM_DIALOG_LOGOUT.REJECT'),
          accept: () => {
            this.logout();
          },
        }),
      );
    }

    if (event.value.action === 'info') {
      this.store.dispatch(new UiActions.openAppInfo());
    }
  }

  logout() {
    this.router.navigateByUrl('/logout');
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
